import type { FC } from 'react';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { getEmojiValueFromContent } from '@confluence/emoji-title/entry-points/transformer.ts';
import { useElementSeenCallback } from '@confluence/analytics/entry-points/useElementSeenCallback.ts';
import { LivePagePreloader } from '@confluence/live-page-preloader/entry-points/LivePagePreloader.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { expValEquals } from '@confluence/feature-experiments';

import { getContentLink, getSpaceLink, getUserAccountId } from '../feedCard-helpers.ts';
import { Wrapper } from '../FeedCard/FeedCard.tsx';
import { FeedCardBody } from '../FeedCard/FeedCardBody.tsx';
import { FeedCardInvisibleFullSizedLink } from '../FeedCard/FeedCardInvisibleFullSizedLink.tsx';
import { FeedCardContext } from '../FeedCard/FeedCardContext.ts';
import { useFeedCardAnalyticsEvents } from '../useFeedCardAnalyticsEvents.ts';
import { useIsMounted } from '../useIsMounted.ts';
import type { FilterByLabelFeedQuery$data } from '../../__generated__/FilterByLabelFeedQuery.graphql.ts';
import type { PopularFeedTimeGranularityValue } from '../../Feed.tsx';
import type { PopularFeedItem } from '../../popularFeedTypes.ts';

const avatarWrapperStyles = xcss({
	paddingRight: 'space.200',
});

// Extract the search result type from the generated query data
type SearchResult = NonNullable<NonNullable<FilterByLabelFeedQuery$data['search']>['nodes']>[0];

type FeedNodeType = PopularFeedItem | SearchResult;

// Content type that may have aiProperty (only PopularFeedItem has it)
type ContentWithOptionalAiProperty = NonNullable<PopularFeedItem['content']>;

type FeedCardProps = {
	feedItem: FeedNodeType;
	ranking: number;
	timeGranularity?: PopularFeedTimeGranularityValue;
	disableAnimation?: boolean;
};

export const PopularFeedCard: FC<FeedCardProps> = memo(
	({ feedItem, ranking, disableAnimation, timeGranularity }) => {
		const isMounted = useIsMounted();
		const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
		const bottomOfCardRef = useRef<HTMLDivElement | null>(null);
		const [hasBeenHovered, setHasBeenHovered] = useState(false);

		const authorName = feedItem?.content?.history?.createdBy?.displayName || '';
		const authorId = getUserAccountId(feedItem?.content?.history?.createdBy) || '';
		const authorSrc = feedItem?.content?.history?.createdBy?.profilePicture?.path || '';
		const ownerName = feedItem?.content?.history?.ownedBy?.displayName || '';
		const ownerId = getUserAccountId(feedItem?.content?.history?.ownedBy) || '';
		const ownerSrc = feedItem?.content?.history?.ownedBy?.profilePicture?.path || '';
		const contentUrl = getContentLink(feedItem?.content);
		const contentId = feedItem?.content?.id || '';
		const contentType = feedItem?.content?.type || 'page';
		const contentSubType = feedItem?.content?.subType;
		const contentTitle = feedItem?.content?.title || '';
		const contentExcerpt = feedItem?.content?.excerpt;
		const creationDate = feedItem?.content?.history?.createdDate || '';
		const friendlyWhen = feedItem?.content?.version?.friendlyWhen || '';
		const spaceName = feedItem?.content?.space?.name || '';
		const spaceId = feedItem?.content?.space?.id || '';
		const spaceKey = feedItem?.content?.space?.key || undefined;
		const spaceUrl = getSpaceLink(feedItem?.content);
		const numComments = feedItem?.content?.metadata?.comments?.commentsCount || 0;
		const emoji = getEmojiValueFromContent(feedItem?.content);
		const lastModifiedDate = feedItem?.content?.metadata?.lastModifiedDate || '';
		const isLivePage = contentType === 'page' && contentSubType === 'live';
		const aiSnippet = (feedItem?.content as ContentWithOptionalAiProperty | null | undefined)
			?.aiProperty?.objectData;

		const hasAiSnippet =
			aiSnippet &&
			expValEquals('confluence_ai_snippets_in_following_popular_feeds', 'isEnabled', true);

		const summaryContent = hasAiSnippet ? aiSnippet : contentExcerpt;

		const handleMouseEnter = React.useCallback(() => {
			setHasBeenHovered(true);
		}, [setHasBeenHovered]);

		const fireAnalyticsEvent = useFeedCardAnalyticsEvents(contentId, spaceId, 'popular', {
			ranking,
			contentType,
			creationDate: new Date(creationDate).getTime(),
			commentsCount: numComments,
			popularFeedTimeGranularity: timeGranularity,
		});

		const onObserved = useCallback(() => {
			fireAnalyticsEvent({
				type: 'sendTrackEvent',
				action: 'observed',
				actionSubject: 'popular-feedCard',
			});
		}, [fireAnalyticsEvent]);

		useElementSeenCallback(bottomOfCardRef, onObserved);

		const feedCardContextValue = useMemo(() => ({ fireAnalyticsEvent }), [fireAnalyticsEvent]);

		if (!contentId) {
			return null;
		}

		// check if page owner exists and content is not a blog
		// checking against the name here for the case of anonymous users
		const shouldUsePageOwner = ownerName && contentType === 'page';

		const preloadOptions = Boolean(SPACE_OVERVIEW.match(contentUrl))
			? PreloadingPresets.onlyJSAndCriticalData
			: PreloadingPresets.onlyJS;

		return (
			<FeedCardContext.Provider value={feedCardContextValue}>
				<LivePagePreloader isLivePage={isLivePage}>
					<HoverPreloader
						name="popular-feed-card"
						preloadUrl={contentUrl}
						preloadOptions={preloadOptions}
					>
						<Wrapper
							data-testid="popular-feed-card"
							isActionMenuOpen={isActionMenuOpen}
							disableAnimation={isMounted || disableAnimation}
							onMouseEnter={handleMouseEnter}
							onFocus={handleMouseEnter}
							data-vc="popular-feed-card"
						>
							<FeedCardInvisibleFullSizedLink
								href={contentUrl}
								popularFeedTimeGranularity={timeGranularity}
								hasAiSnippet={!!hasAiSnippet}
							/>
							<Box xcss={avatarWrapperStyles}>
								<ProfileAvatar
									userId={shouldUsePageOwner ? ownerId : authorId}
									src={shouldUsePageOwner ? ownerSrc : authorSrc}
									canViewProfile
									size="medium"
								/>
							</Box>
							<FeedCardBody
								toggleActionMenu={setIsActionMenuOpen}
								emoji={emoji}
								contentId={contentId}
								contentTitle={contentTitle}
								contentType={contentType}
								contentSubType={contentSubType}
								contentUrl={contentUrl}
								authorName={authorName}
								authorId={authorId}
								ownerName={ownerName}
								ownerId={ownerId}
								spaceId={spaceId}
								spaceKey={spaceKey}
								spaceName={spaceName}
								spaceUrl={spaceUrl}
								contentExcerpt={summaryContent}
								numComments={numComments}
								popularCardLayout
								timestamp={friendlyWhen}
								hasBeenHovered={hasBeenHovered}
								lastModifiedDate={lastModifiedDate}
								source="popular-feed"
								popularFeedTimeGranularity={timeGranularity}
								hasAiSnippet={!!hasAiSnippet}
							/>
							<div ref={bottomOfCardRef} />
						</Wrapper>
					</HoverPreloader>
				</LivePagePreloader>
			</FeedCardContext.Provider>
		);
	},
);
